import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import i18n from '../../../i18n/i18n';
import Tooltip from '../../../components/Tooltip';
import LazyImage from '../../../components/LazyImage';

import ParkingAreaPlaceholderImage from "../../../images/parking-icon-placeholder.png";
import WalkingIcon from "../../../images/mapSearch/walking-icon.svg";
import FloatingIcon from "../../../images/mapSearch/floating-graphic.svg";
import ReservedIcon from "../../../images/mapSearch/reserved-graphic.svg";
import ResidentialIcon from "../../../images/mapSearch/residential-graphic.svg";
import CommercialIcon from "../../../images/mapSearch/commercial-graphic.svg";
import HotBadge from "../../../images/mapSearch/hot-badge.png";
import EvBadge from "../../../images/mapSearch/ev-badge.png";

import { centToDollar } from "../../../utils/stringFormatter";

const ParkingAreaItem = ({
  parkingArea,
  distance,
  hoveringParkingAreaId,
  onClick,
  onHover,
}) => {
  const price = parkingArea?.displayMonthlyPrice?.avg ? centToDollar(parkingArea?.displayMonthlyPrice?.avg) : 'N/A';
  const availableBay = (parkingArea.additionalInformation.totalVacant ?? 0);

  return (
    <button onClick={onClick} className={`flex justify-between items-center py-4 px-5 text-left border-b border-b-[#ebeaea] ${hoveringParkingAreaId === parkingArea.id ? 'bg-[#f1f0f0]' : ''}`} onMouseOver={onHover}>
      <div className="flex flex-col mr-[5px]">
        <div className="flex flex-row mb-[10px]">
          {parkingArea.additionalInformation.hasEvCharging && (
            <Tooltip placement="right" tooltip={i18n.t("findParking.evCharging")}>
              <LazyImage className="w-[45px] mr-[5px]" src={EvBadge} alt="ev-icon" />
            </Tooltip>
          )}
          {parkingArea.additionalInformation.isHot && (
            <Tooltip placement="right" tooltip={i18n.t("findParking.highDemand")}>
              <LazyImage className="w-[45px]" src={HotBadge} alt="hot-icon" />
            </Tooltip>
          )}
        </div>
        <h4 className="text-[14px] sm:text-[16px] font-medium">{parkingArea.name}</h4>
        <div className="flex flex-wrap gap-1 items-end my-[5px]">
          <span className="text-black text-[11px] leading-3">{i18n.t("findParking.price")}</span>
          <span className="text-[14px] font-bold text-black leading-3 sm:text-[16px]">{`${parkingArea.currency ?? 'MYR'} ${price}`}</span>
        </div>
        {parkingArea.additionalInformation.totalVacant > 0 ? (
          <p className="mb-[3px] text-[11px] sm:mb-[5px]">{`${availableBay} ${i18n.t("findParking.availableBay")}`}</p>
        ) : (
          <div className="flex items-center flex-wrap gap-[2px] mr-[10px] sm:gap-[5px]">
            <p className="text-[#0d59bd] text-[11px] font-medium sm:text-[12px]">
              {i18n.t("findParking.fullyBooked")}
            </p>
            <div className="text-[#0d59bd]">
              <Tooltip name={parkingArea.id} placement="top" trigger="hover" tooltip={i18n.t("findParking.waitingList")}>
                <FontAwesomeIcon className="w-[14px] sm:w-[16px]" icon={faInfoCircle} />
              </Tooltip>
            </div>
          </div>
        )}
        <div className="flex items-center flex-wrap gap-3">
          {parkingArea.additionalInformation?.hasReserved && (
            <div className="flex items-center gap-1">
              <img src={ReservedIcon} className="h-5 object-contain" alt='reserved' />
              <span className="text-[11px] leading-none">{i18n.t("findParking.carparkType.reserved")}</span>
            </div>
          )}
          {parkingArea.additionalInformation?.hasFloating && (
            <div className="flex items-center gap-1">
              <img src={FloatingIcon} className="h-5 object-contain" alt='floating' />
              <span className="text-[11px] leading-none">{i18n.t("findParking.carparkType.floating")}</span>
            </div>
          )}
          {parkingArea.additionalInformation?.hasCommercial && (
            <div className="flex items-center gap-1">
              <img src={CommercialIcon} className="h-5 object-contain" alt='commercial' />
              <span className="text-[11px] leading-none">{i18n.t("findParking.carparkType.commercial")}</span>
            </div>
          )}
          {(parkingArea.additionalInformation?.hasApartment || parkingArea.additionalInformation?.hasLanded) && (
            <div className="flex items-center gap-1">
              <img src={ResidentialIcon} className="h-5 object-contain" alt='residential' />
              <span className="text-[11px] leading-none">{i18n.t("findParking.carparkType.residential")}</span>
            </div>
          )}
        </div>
        {distance && distance.status === "OK" && (
          <div className="flex gap-2 mt-1 items-center pointer-events-none">
            <img className="w-[15px]" src={WalkingIcon} alt="walkingicon" />
            <div>
              <p className="text-[10px] text-[#929292]">{i18n.t("findParking.carToProperty")}</p>
              <p className="text-[12px] font-medium">{distance.duration.text} &nbsp; &nbsp; {distance.distance.text}</p>
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <img className="w-[95px] h-[95px] shadow object-cover rounded" src={parkingArea?.additionalInformation?.images?.length > 0 ? parkingArea.additionalInformation.images[0].url : ParkingAreaPlaceholderImage} alt={parkingArea.name} />
      </div>
    </button>
  )
}

export default ParkingAreaItem;